import type { Key } from 'ant-design-vue/lib/vc-table/interface'
import { useAPI } from '~/composables/useApi'
import type { EligibilityResponse } from '~/components/My-Cover/migration/eligibility-dialog.vue'
import type { ClaimAnalysisResponse, RecordSchema } from '@/types'

export type ComputePolicyRemovalMetrics = {
	total_policies_to_be_removed: number
	new_plan_premium: number
	total_policies_now_covered: number
	plan_renewal_date: string
	plan: PlanInformationContext
}

export default function usePurchaseApi() {
	const allPurchases = async <T = PurchaseContext[]>(
		query?: Record<string, unknown>,
		options?: Record<string, unknown>
	) => {
		const { data } = await useAPI<IRequestResponse<PaginationContext<T>>>('/purchases', {
			query,
			...(options || {})
		})
		return data.value?.data ?? null
	}
	const purchasesInfo = async () => {
		const { data } = await useAPI<IRequestResponse<PurchaseInfo>>('/purchases/purchase-summary')
		return data.value
	}

	const getPurchasePolicies = async (purchase_id?: string, query?: RecordSchema) => {
		const { data } = await useAPI<IRequestResponse<PaginationContext<PolicyResponse[]>>>(
			`/purchases/${purchase_id ?? ''}/policies`,
			{
				query: { ...(query ?? {}) }
			}
		)
		return data.value
	}

	const getPolicies = async <T = PolicyResponse[]>(
		query: { is_deactivated?: boolean; [id: string]: unknown } = { is_active: true }
	) => {
		const { data } = await useAPI<IRequestResponse<PaginationContext<T>>>('/policies', {
			query
		})
		return data.value?.data ?? null
	}
	const getDeactivatedPolicies = async <T = PolicyResponse[]>(query?: {
		purchase_id?: string
		[id: string]: unknown
	}) => {
		const { data } = await useAPI<IRequestResponse<PaginationContext<T>>>('/policies/deactivated-policies', {
			query: { ...(query ?? {}) }
		})
		return data.value?.data ?? null
	}

	const getPurchaseDetail = async <T = PlanInformationContext>(purchase_id: string, query?: object) => {
		const { data } = await useAPI<IRequestResponse<T>>(`/purchases/${purchase_id || ''}`, { query })
		if (data.value?.data) {
			const result = data.value.data as unknown as PlanInformationContext
			const productName = String(result?.product.name).toLowerCase()
			const productCategory = result?.product.productCategory.name.toLowerCase() || ''
			return {
				...data.value,
				data: {
					...data.value.data,
					// Prevent travel from renewable list
					renewable: !productName.includes('travel'),
					is_claimable: ['auto', 'gadget'].includes(productCategory) || productName.includes('travel')
				}
			}
		}
		return data.value
	}
	const renewPlan = async <T = unknown>(
		url: string,
		payload: {
			purchase_id: string
			payment_channel: PaymentGate
			sales_type: 'renewal'
			transaction_id?: string
			product_id?: string
			promo_code?: string
		}
	) => {
		const { data } = await useAPI<IRequestResponse<T>>(url, {
			body: payload,
			method: 'POST'
		})
		return data.value
	}
	const queryPolicy = async <T = PolicyResponse>(policy_id: string) => {
		const { data } = await useAPI<IRequestResponse<T>>(`/policies/${policy_id}`)
		return data.value
	}
	const removePolicySummary = async (payload: Key[]) => {
		const { data } = await useAPI<IRequestResponse<ComputePolicyRemovalMetrics>>('/policies/remove-summary', {
			body: {
				policy_ids: payload
			},
			method: 'POST'
		})
		return data.value
	}
	const removePolicy = async <T = unknown>(
		status: '' | 'pending-policies' | 'policies' = '',
		payload: Key[],
		password: string,
		query?: object
	) => {
		// Check for policy status and toggle between endpoints
		const payloadData =
			status === 'pending-policies'
				? {
						pending_policy_ids: payload,
						password
					}
				: {
						policy_ids: payload,
						password
					}
		const { data } = await useAPI<IRequestResponse<T>>(`/${status || 'policies'}/remove`, {
			body: payloadData,
			method: 'POST',
			query
		})
		return data.value
	}
	const removePendingPolicySummary = async (payload: Key[]) => {
		const { data } = await useAPI<IRequestResponse<ComputePolicyRemovalMetrics>>(
			'/pending-policies/remove-summary',
			{
				body: {
					pending_policy_ids: payload
				},
				method: 'POST'
			}
		)
		return data.value
	}
	const computePlanUpgrade = async <T = ComputedPlanUpgradeResponse | EligibilityResponse>(
		purchase_id: string,
		payload: { product_id: string; month: string; policies?: string[]; pending_policies?: string[] },
		computeType: 'plan-upgrade-summary' | 'compute-plan-upgrade' | '' = 'plan-upgrade-summary',
		source?: string,
		query = {}
	) => {
		const { data } = await useAPI<IRequestResponse<T>>(`/${source || 'auto'}/${purchase_id}/${computeType}`, {
			body: payload,
			method: 'PUT',
			query
		})
		return data.value
	}
	const computePlanDowngrade = async <T = ComputedPlanUpgradeResponse | EligibilityResponse>(
		purchase_id: string,
		payload: { product_id: string; month: string; policies?: string[]; pending_policies?: string[] },
		computeType: 'plan-downgrade-summary' | 'compute-plan-downgrade' | '' = 'plan-downgrade-summary',
		source?: string,
		query = {}
	) => {
		const { data } = await useAPI<IRequestResponse<T>>(`/${source || 'auto'}/${purchase_id}/${computeType}`, {
			body: payload,
			method: 'PUT',
			query
		})
		return data.value
	}
	const computeEditCoverPeriod = async <T = EditCoverPeriodComputedResponse>(payload: {
		month: string | number
		plan_id: string
		product_id: string
		promo_code?: string
	}) => {
		const { data } = await useAPI<IRequestResponse<T>>(`/purchases/${payload.plan_id}/compute-cover-period`, {
			body: payload,
			method: 'PUT'
		})
		return data.value
	}
	const computeNewDependantSlot = async <T = unknown>(payload: ComputeAddDependantDto) => {
		const { data } = await useAPI<IRequestResponse<T>>('/purchases/compute-business-add-dependant', {
			body: payload,
			method: 'POST'
		})
		return data.value
	}
	const addNewDependantSlot = async <T = unknown>(payload: AddDependantDTO) => {
		const { data } = await useAPI<IRequestResponse<T>>('/purchases/business-add-dependant-no-slot', {
			body: payload,
			method: 'POST'
		})
		return data.value
	}

	const updatePlanDetails = async (
		categoryName = 'auto',
		type: 'upgrade' | 'downgrade',
		plan_id: string,
		payload: {
			payment_channel: string
			product_id: string
			month: string | number
			pending_policies?: string[]
			policies?: string[]
			transaction_id?: string
		}
	) => {
		const { data } = await useAPI<IRequestResponse<unknown>>(`/${categoryName}/${plan_id}/${type}`, {
			body: { ...payload, sales_type: `plan ${type}` },
			method: 'POST'
		})
		return data.value
	}
	const updatePeriodOfCover = async (payload: {
		month: number | string
		product_id?: string
		payment_channel?: string
		sales_type?: string
		transaction_id?: string
		plan_id: string
	}) => {
		const { data } = await useAPI<IRequestResponse<EditCoverPeriodComputedResponse>>(
			`/purchases/${payload.plan_id}/edit-cover-period`,
			{
				body: payload,
				method: 'POST'
			}
		)
		return data.value
	}
	const addDependant = async (payload: DependantPayload) => {
		const { data } = await useAPI<IRequestResponse<PolicyResponse['meta']['dependants']>>(
			'/policies/add-dependant',
			{
				method: 'POST',
				body: payload
			}
		)
		return data.value
	}
	const removeDependant = async (payload: { policy_id: string }, query?: object) => {
		const { data } = await useAPI<IRequestResponse<PolicyResponse['meta']['dependants']>>(
			'/policies/remove-dependant',
			{
				method: 'POST',
				body: payload,
				query
			}
		)
		return data.value
	}
	const purchaseTransactions = async (plan_id: string) => {
		const { data } = await useAPI<IRequestResponse<PaginationContext<PurchaseTransaction[]>>>(
			`/purchases/${plan_id}/transactions`
		)
		return data.value
	}
	const purchaseTransaction = async (purchase_id: string, transaction_id: string) => {
		const { data } = await useAPI<IRequestResponse<PurchaseTransaction>>(
			`/purchases/${purchase_id}/transactions/${transaction_id}`
		)
		return data.value
	}
	const planInformation = async (plan_id: string, query?: Record<string, unknown>) => {
		const { data } = await useAPI<IRequestResponse<unknown>>(
			`/purchases/${plan_id}/plan-info?${createQueryParam(query || {})}`
		)
		return data.value
	}

	const getPlanUsageMetrics = async (purchase_id: string, query: Record<string, unknown>) => {
		const { data } = await useAPI<IRequestResponse<PlanUsageInfo>>(
			`/purchases/${purchase_id}/plan-usage-overview`,
			{ query }
		)
		return data.value
	}
	const getPlanUsageRecords = async (purchase_id: string, query?: Record<string, unknown>) => {
		const { data } = await useAPI<IRequestResponse<UsageRecordsContext>>(
			`/purchases/${purchase_id}/plan-usage-per-policy`,
			{ query }
		)
		return data.value
	}
	const getPlanUsageChart = async (purchase_id: string, query: Record<string, unknown>) => {
		const { data } = await useAPI<IRequestResponse<PlanUsageCharts>>(
			`/purchases/${purchase_id}/claims-comparison`,
			{
				query
			}
		)
		return data.value
	}

	const getClaimsAnalysis = async <T = ClaimAnalysisResponse>(purchase_id: string, plan_category: string) => {
		const { data } = await useAPI<IRequestResponse<T>>(`/purchases/${purchase_id}/${plan_category}-claims-analysis`)
		return data.value
	}
	const planHistory = async <T = IRequestResponse<PlanInformationActivities>>(
		purchase_id: string,
		query?: Record<string, unknown>
	) => {
		const { data } = await useAPI<T>(`/purchases/${purchase_id}/plan-activities-history-overview`, {
			query
		})
		return data.value
	}
	const planActivitiesMetrics = async (purchase_id: string) => {
		const { data } = await useAPI<IRequestResponse<PurchasesActivitiesMetrics>>(
			`/purchases/${purchase_id}/plan-activities`
		)
		return data.value
	}
	const planTransactionsHistory = async (purchase_id: string, query: Record<string, unknown>) => {
		const { data } = await useAPI<IRequestResponse<PlanUsageHistoryResponse>>(
			`/purchases/${purchase_id}/plan-history`,
			{
				query
			}
		)
		return data.value
	}
	const getPolicyDependants = async (policy_id: string, query?: object) => {
		const queryVal = toRef(query)
		const { data } = await useAPI<IRequestResponse<CustodianDetails['dependants'] | PolicyResponse[]>>(
			`/policies/${policy_id}/dependants`,
			{ query: queryVal.value }
		)
		return data.value
	}
	const computeDependantSummary = async (policy_id: string) => {
		const { data } = await useAPI<IRequestResponse<dependantsRemovalSummaryResponse>>(
			'/policies/remove-dependant-summary',
			{
				method: 'POST',
				body: { policy_id }
			}
		)
		return data.value
	}
	/****
	 * @description Use this endpoint to confirm if a health user already has an existing health plan attached to them.
	 *  */
	const comparePolicy = async (purchase_id: string, customer_id: string) => {
		const { data } = await useAPI<
			IRequestResponse<{
				newPendingPolicyToActivate: PendingPolicyResponse
				existingPolicy: PolicyResponse
			}>
		>(`/purchases/${purchase_id}/compare-plans`, { query: { customer_id } })

		return data.value
	}

	const fetchInactivePolicies = async (arg: { purchase_id: string; policy_id?: string }) => {
		const { data } = await useAPI<IRequestResponse<PaginationContext<PolicyResponse[]>>>(
			`inactive-pending-policies/${arg?.policy_id || ''}`,
			{
				query: arg
			}
		)
		return data.value?.data ?? null
	}
	const fetchDeactivatePolicies = async <T = PaginationContext<PolicyResponse>>(
		purchase_id: string,
		query: { year: string; month: string } & Record<string, unknown>
	) => {
		const { data } = await useAPI<IRequestResponse<T>>(`/policies/${purchase_id}/deactivated-history`, {
			query
		})
		return (data.value ?? null) as T
	}
	const filterPolicies = async <T = PaginationContext<PolicyResponse>>(
		purchase_id: string,
		query: { year: string; month: string } & Record<string, unknown>
	) => {
		const { data } = await useAPI<IRequestResponse<T>>(`/policies/${purchase_id}/policy-history`, {
			query
		})
		return (data.value ?? null) as T
	}

	const verifyTableUserRecord = async (body: { email: string; phone: string }) => {
		const { data } = await useAPI<IRequestResponse<boolean>>('/users/verify-email-phone-link', {
			method: 'POST',
			body
		})
		return data.value
	}

	return {
		getDeactivatedPolicies,
		getPolicies,
		allPurchases,
		renewPlan,
		getPurchasePolicies,
		getPurchaseDetail,
		queryPolicy,
		purchasesInfo,
		removePolicySummary,
		removePendingPolicySummary,
		removePolicy,
		computePlanUpgrade,
		computePlanDowngrade,
		updatePlanDetails,
		addDependant,
		verifyTableUserRecord,
		removeDependant,
		purchaseTransaction,
		purchaseTransactions,
		planInformation,
		computeEditCoverPeriod,
		updatePeriodOfCover,
		getPlanUsageMetrics,
		getClaimsAnalysis,
		getPlanUsageChart,
		planHistory,
		planActivitiesMetrics,
		getPlanUsageRecords,
		planTransactionsHistory,
		getPolicyDependants,
		computeDependantSummary,
		computeNewDependantSlot,
		addNewDependantSlot,
		comparePolicy,
		fetchInactivePolicies,
		fetchDeactivatePolicies,
		filterPolicies
	}
}
